import { Box, Paper, Typography, Accordion,
  AccordionSummary,
  AccordionDetails,
  ExpandMore, } from "@mui/material";
import moment from "moment";
import React, { useEffect } from "react";
import {
  PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer,
} from "recharts";
import DateRangePickerComponent from "../../../core/components/common/custom-date-range-picker.component";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import EmptyBox from "../../../assets/icons/empty-box.png";

export default function GatepassAnalyticsComponent(props) {
  const { data, handleDateRangeChange, startDate, endDate, orgName, getOrgNameByOrgUID } = props;

  const [mappedGatepassCount, setMappedGatepassCount] = React.useState([]);

  const statusColorMap = {
    "For Approval": "#2196f3",
    "Active": "#4caf50",
    "Suspended": "#ff9800",
    "Denied": "#f44336",
    "Deactivated": "#6B7280",
    "Inactive": "#6B7280",
  };

  useEffect(() => {
      const orgUIDs = data
        ?.map(item => item.name)
        .filter(name => name !== 'PPA');
  
      if (orgUIDs?.length > 0) {
        getOrgNameByOrgUID({ org_uids: orgUIDs });
      }
    }, [data]);
  
  
     useEffect(() => {
    if (!data || !orgName) return;
  
    const mapped = data?.map(item => {
      // Keep 'PPA' as is or if name not found
      if (item.name === 'PPA' || !item.name) return item;
  
      return {
        ...item,
        name: orgName[item.name] || item.name,
      };
    });
  
    setMappedGatepassCount(mapped);
  }, [data, orgName]);

  // Group data by status with total count and vehicle names
  const groupedData = mappedGatepassCount?.reduce((acc, curr) => {
    const { status, name, count } = curr;
    if (!acc[status]) {
      acc[status] = { status, value: 0, vehicles: [] };
    }
    acc[status].value += count;
    acc[status].vehicles.push(`${name} (${count})`);
    return acc;
  }, {}) || {}; // <-- fallback to empty object if data is undefined

  const aggregatedData = Object.values(groupedData);

  return (
    <Box>
      <Paper elevation={3} sx={{ p: 3, borderRadius: 3, height: 589}}>
        {/* Header & Date Picker */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" gutterBottom>Gatepass Component</Typography>
          <Box sx={{ flexGrow: 1 }} />
          <DateRangePickerComponent
            initialStartDate={startDate}
            initialEndDate={endDate}
            ranges={{
              "Today": [moment().startOf('day'), moment().endOf('day')],
              "Last 7 Days": [moment().subtract(6, "days").startOf('day'), moment().endOf('day')],
              "This Month": [moment().startOf("month"), moment().endOf("month")],
              "Last Month": [
                moment().subtract(1, "month").startOf("month"),
                moment().subtract(1, "month").endOf("month"),
              ],
              "Last 90 Days": [moment().subtract(89, "days").startOf('day'), moment().endOf('day')],
            }}
            onDateRangeChange={handleDateRangeChange}
          />
        </Box>
        {aggregatedData?.length > 0 ? (
        <Box >
          <ResponsiveContainer width="100%" height={400}>
            <PieChart>
              <Pie
                data={aggregatedData}
                dataKey="value"
                nameKey="status"
                cx="50%"
                cy="50%"
                outerRadius={120}
                label={({ name, value }) =>
                  `${name} | count: ${value}`
                }
                activeIndex={-1}  
              >
                {aggregatedData?.map((entry, index) => (
                  <Cell
                    focusable={false}
                    key={`cell-${index}`}
                    fill={statusColorMap[entry.status] || "#ccc"}
                  />
                ))}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>

          <Accordion sx={{ mt: 4, zIndex: 1200 }} >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle1" fontWeight="bold">Status Breakdown</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{height: 400, overflowY: "auto", }}>
            {aggregatedData?.map((entry) => (
              <Box
                key={entry.status}
                sx={{
                  backgroundColor: `${statusColorMap[entry.status]}20`,
                  color: statusColorMap[entry.status],
                  p: 2,
                  mb: 2,
                  borderRadius: 2,
                  maxHeight: 300, overflowY: 'auto'
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1,  }}>
                  <Typography variant="body1" fontWeight="bold">{entry.status}</Typography>
                  <Typography variant="body1" fontWeight="bold">{entry.value}</Typography>
                </Box>
                <Box sx={{ pl: 2 }}>
                  {entry.vehicles.map((vehicle, idx) => (
                    <Typography key={idx} variant="body2" sx={{ color: 'inherit' }}>
                      • {vehicle}
                    </Typography>
                  ))}
                </Box>
              </Box>
            ))}
          </AccordionDetails>
          </Accordion>
        </Box>
        ) : (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              flexDirection: "column",
              minHeight: "300px",
              textAlign: "center",
            }}
          >
            <img src={EmptyBox} alt="No Data" width={120} />
            <Typography variant="subtitle1" color="textSecondary" mt={2}>
              No Data Available
            </Typography>
          </Box>
        )}
      </Paper>
    </Box>
  );
}
